import React, { useState, useEffect, useMemo } from 'react';
import RequestsList from './RequestsList';
import RequestDetails from './RequestDetails';
import ChatSection from './ChatSection';
import { filterAndSortRequests } from '../utils/requestUtils';
import useRequests from '../hooks/useRequests';
import { useUser } from '../UserContext';

const RequestsComponent = ({ type, handleStatusUpdate }) => {
  const { user } = useUser();
  const { inboundRequests, outboundRequests, loading } = useRequests(user?.uid);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('createdAt');
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 640);

  const requests = type === 'Inbound' ? inboundRequests : outboundRequests;

  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const sortedRequests = useMemo(() => {
    return filterAndSortRequests(requests, statusFilter, sortBy);
  }, [requests, statusFilter, sortBy]);

  const handleRequestSelection = (request) => {
    setSelectedRequest(request);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const showFilterSort = !isMobileView || !selectedRequest;

  return (
    <div className="flex flex-col h-full">
      {showFilterSort && (
        <div className="bg-white border rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:space-x-6 space-y-3 sm:space-y-0">
            <div className="w-full sm:w-48">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Status
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="All">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
                <option value="Declined">Declined</option>
              </select>
            </div>
            <div className="w-full sm:w-48">
              <label htmlFor="sort-by" className="block text-sm font-medium text-gray-700 mb-1">
                Sort by
              </label>
              <select
                id="sort-by"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
              >
                <option value="createdAt">Date Created</option>
                <option value="borrowDate">Borrow Date</option>
                <option value="toolName">Tool Name</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-4 min-h-0 flex-grow">
        <div className={`sm:w-2/5 overflow-y-auto transition-all duration-300 ease-in-out
          ${isMobileView && selectedRequest ? 'hidden' : 'block'}`}
        >
          <RequestsList
            requests={sortedRequests}
            selectedRequest={selectedRequest}
            setSelectedRequest={handleRequestSelection}
          />
        </div>

        <div className={`sm:w-3/5 overflow-y-auto transition-all duration-300 ease-in-out
          ${selectedRequest ? 'block' : 'hidden sm:block'}`}
        >
          {selectedRequest ? (
            <div className="flex flex-col gap-4">
              {isMobileView && (
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="inline-flex items-center px-4 py-2 bg-white border rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to List
                </button>
              )}
              
              <RequestDetails
                key={selectedRequest.id}
                request={selectedRequest}
                handleStatusUpdate={handleStatusUpdate}
                isOutbound={type === 'Outbound'}
              />
              
              <div className="bg-white border rounded-lg shadow-sm">
                <ChatSection
                  key={selectedRequest.id}
                  conversationId={selectedRequest.id}
                  userId={user?.uid}
                />
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center bg-white border rounded-lg shadow-sm p-8">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No request selected</h3>
                <p className="mt-1 text-sm text-gray-500">Select a request from the list to view details and chat</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestsComponent;
